<template>
  <v-container ref="companyAccount">
    <!-- Alert box -->
    <AlertBox :title="alertTitle" :text="alertText" v-if="showAlert" @close="resetAlert" />

    <!-- Company Name Header -->
    <h1>{{ getSelectedCompany?.name }}</h1>
    
    <!-- Add User Button -->
    <v-btn class="add-button" @click="openAddUserForm">Add User</v-btn>

    <!-- User List -->
    <div class="user-list">
      <div class="user-row header">
        <div class="user-details">
          <span class="user-name">Name</span>
          <span class="user-email">Email</span>
          <span class="user-dialed-in">Dialed In?</span>
          <span class="user-talking">Talking?</span>
          <span class="user-role">Role</span>
        </div>
      </div>
      <div
        v-for="(user, index) in users"
        :key="user._id"
        class="user-row"
        :class="{ 'selected': selectedUserIndex === index }"
        :style="{ backgroundColor: selectedUserIndex === index ? '#d3d3d3' : index % 2 === 0 ? '#f9f9f9' : '#ffffff' }"
        @click="selectUser(user, index)"
      >
        <!-- User Details -->
        <div class="user-details">
          <span class="user-name">{{ user.name }}</span>
          <span class="user-email">{{ user.email }}</span>
          <span class="user-dialed-in">
            <span v-if="user.isDialedIn" class="status-dot dialed-in">●</span>
            <span v-else>&nbsp;</span>
          </span>
          <span class="user-talking">
            <span v-if="user.talkingTo" class="status-dot talking">●</span>
            <span v-else>&nbsp;</span>
          </span>
          <span class="user-role">{{ user.role }}</span>
        </div>

        <!-- Edit Button -->
        <v-btn class="edit-button" @click.stop="editUser(user)">Edit</v-btn>
        <!-- Delete Button -->
        <v-btn class="delete-button" @click.stop="confirmDeleteUser(user)">Delete</v-btn>
      </div>
    </div>

    <!-- Phone Numbers Panel -->
    <CompanyAccountYourPhoneNumbers :company="getSelectedCompany" />

    <!-- Raw Call Diagnostics -->
    <CompanyAccountRawCallDiagnostics />

    <!-- TwoTrees Call Records Panel (only for company 1014) -->
    <CompanyAccountTwoTreesCallLogs v-if="getSelectedCompany?.companyNumber === 1014" />

    <!-- A-Affordable SMS Panel (only for company 1008) -->
    <CompanyAccountAABBSms v-if="getSelectedCompany?.companyNumber === 1008" />

    <!-- Indemnitor Packets Panel (companies 1001 — Pacific Northwest Bail Bonding, 1008 — A-Affordable, 1002 - BBAS, 1019 - A1 Bonding) -->
    <CompanyAccountIndemnitor v-if="[1001, 1002, 1008, 1019].includes(getSelectedCompany?.companyNumber)" />

    <!-- Pricing Panel -->
    <CompanyAccountPricingAndBilling />

    <!-- Documents Panel -->
    <CompanyAccountDocumentsPoliciesAgreements 
      :company="getSelectedCompany"
    />

    <!-- Edit Company Profile Form Dialog -->
    <v-dialog v-model="companyProfileForm" max-width="500px">
      <v-card>
        <v-card-title>Edit Company Profile</v-card-title>
        <v-card-text>
          <v-text-field v-model="companyForm.name" label="Company Name" readonly></v-text-field>
          <v-text-field v-model="companyForm.bbasMainPhone" label="Main BBAS Dial-In Number" readonly></v-text-field>
          <v-text-field v-model="companyForm.intakeNotificationEmails" label="Intake Notification Emails" outlined></v-text-field>
          <v-file-input v-model="companyForm.icon" label="Upload Company Icon" accept="image/*" disabled></v-file-input>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeCompanyProfileForm">Cancel</v-btn>
          <v-btn text color="primary" @click="submitCompanyForm">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Permission Dialog -->
    <v-dialog v-model="dialog" max-width="400px">
      <v-card>
        <v-card-title>Permission Alert</v-card-title>
        <v-card-text>
          {{ dialogMessage }}
        </v-card-text>
        <v-card-actions>
          <v-btn color="primary" text @click="closeDialog">OK</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Add/Edit User Form Dialog -->
    <v-dialog v-model="showFormDialog" max-width="500px">
      <v-card>
        <v-card-title>{{ isEditMode ? 'Edit User' : 'Add User' }}</v-card-title>
        <v-card-text>
          <v-text-field 
            v-model="form.name" 
            label="Name" 
            required
            :error-messages="form.name.trim() === '' && showValidationAnimation ? 'Name is required' : ''"
            :class="{ 'shake-animation': showValidationAnimation && form.name.trim() === '' }"
          ></v-text-field>
          <v-text-field 
            v-model="form.email" 
            label="Email" 
            :disabled="isEditMode" 
            required
            :error-messages="!isEditMode && form.email.trim() === '' && showValidationAnimation ? 'Email is required' : ''"
            :class="{ 'shake-animation': showValidationAnimation && !isEditMode && form.email.trim() === '' }"
          ></v-text-field>
          <v-select
            v-model="form.role"
            :items="roleOptions"
            label="Role"
            required
            :error-messages="form.role === '' && showValidationAnimation ? 'Role is required' : ''"
            :class="{ 'shake-animation': showValidationAnimation && form.role === '' }"
          ></v-select>
          <v-text-field v-model="getSelectedCompany.name" label="Company" disabled></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeForm">Cancel</v-btn>
          <v-btn 
            text 
            :color="submitClicked ? 'error' : 'primary'" 
            @click="submitForm" 
            :disabled="!isAddUserFormValid || submitClicked"
          >
            {{ submitClicked ? 'Wait...' : (isEditMode ? 'Save' : 'Add') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="showDeleteDialog" max-width="400px">
      <v-card>
        <v-card-title>Confirm Deletion</v-card-title>
        <v-card-text>
          Are you sure you want to delete this user?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeDeleteDialog">Cancel</v-btn>
          <v-btn text color="red" @click="deleteUser">Yes, Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import axios from 'axios';
import { mapState, mapGetters, mapActions } from 'vuex';
import AlertBox from '@/components/AlertBox.vue';
import CompanyAccountYourPhoneNumbers from '@/components/CompanyAccount-YourPhoneNumbers.vue';
import CompanyAccountTwoTreesCallLogs from '@/components/CompanyAccount-TwoTreesCallLogs.vue';
import { eventService } from '../services/EventService';
import { prettyPhone } from '../utils/helpers';
import CompanyAccountDocumentsPoliciesAgreements from '@/components/CompanyAccount-DocumentsPoliciesAgreements.vue';
import CompanyAccountPricingAndBilling from '@/components/CompanyAccount-PricingAndBilling.vue';
import CompanyAccountRawCallDiagnostics from '@/components/CompanyAccount-RawCallDiagnostics.vue';
import CompanyAccountAABBSms from '@/components/CompanyAccount-AABBSms.vue';
import CompanyAccountIndemnitor from '@/components/CompanyAccount-Indemnitor.vue';

export default {

 name: 'CompanyAccount',

 components: {
   AlertBox,
   CompanyAccountYourPhoneNumbers,
   CompanyAccountTwoTreesCallLogs,
   CompanyAccountDocumentsPoliciesAgreements,
   CompanyAccountPricingAndBilling,
   CompanyAccountRawCallDiagnostics,
   CompanyAccountAABBSms,
   CompanyAccountIndemnitor
 },

 data() {
   return {
     submitClicked: false,
     usersEventSource: null,
     showAlert: false, 
     alertTitle: '',
     alertText: '',
     dialog: false,
     dialogMessage: '',
     companyProfileForm: false,
     users: [],
     showFormDialog: false,
     isEditMode: false,
     form: {
       _id: null,
       name: '',
       email: '',
       role: ''
     },
     companyForm: {
       name: '',
       bbasMainPhone: '',
       intakeNotificationEmails: '',
       icon: null,
     },
     selectedUserIndex: null,
     showDeleteDialog: false,
     selectedUser: null,
     roleOptions: [],
     showValidationAnimation: false
   };
 },

 computed: {
   ...mapState(['user']),
   ...mapGetters(['userRole', 'getSelectedUser', 'getSelectedCompany']),
   isAddUserFormValid() {
     return this.form.name.trim() !== '' && 
            (!this.isEditMode ? this.form.email.trim() !== '' : true) && 
            this.form.role !== '';
   }
 },

 watch: {
 },

 beforeRouteEnter(to, from, next) {
   next(vm => {
     vm.fetchSelectedCompany();
   });
 },

 beforeRouteUpdate(to, from, next) {
   this.fetchSelectedCompany();
   next();
 },

 methods: {
   ...mapActions(['setSelectedUser', 'setSelectedCompany', 'updateSelectedCompany', 'updateSelectedUser']),

   prettyPhone(phone) {
     return prettyPhone(phone);
   },

   async fetchSelectedCompany() {
     try {
       const companyNumber = this.getSelectedCompany?.companyNumber;
       if (!companyNumber) return;
        
       const response = await axios.get(`/portaldb/db/companies/${companyNumber}`);
       this.setSelectedCompany(response.data);
     } catch (error) {
       console.error('Error fetching selected company:', error);
       this.alertTitle = "Error";
       this.alertText = "Failed to fetch company data. Please try again.";
       this.showAlert = true;
     }
   },

   updateOrInsertUser(updatedUserRecord) {
     const index = this.users.findIndex(userRecord => userRecord.uid === updatedUserRecord.uid);
     if (index !== -1) {
       this.users[index] = updatedUserRecord;
     } else {
       this.users.unshift(updatedUserRecord);
     }
   },

   closeUsersEventSource() {
     console.log('close users event source');
     if (this.usersEventSource) {
       this.usersEventSource.close();
     }
   },

   handleUsersEvent(event) {
     const data = JSON.parse(event.data);
     delete data._id;
     this.updateOrInsertUser(data);
     if (this.getSelectedUser && data.uid == this.getSelectedUser.uid) {
       this.updateSelectedUser(data);
     }
   },

   listenForUsersUpdates() {
     const events = eventService.getConnection(this.getSelectedCompany.companyNumber);
     if (events) {
       events.addEventListener('users', (event) => this.handleUsersEvent(event));
     }
   },

   unlistenForUsersUpdates() {
     const selectedCompany = this.getSelectedCompany;
     if (selectedCompany?.companyNumber) {
       const events = eventService.getConnection(selectedCompany.companyNumber);
       if (events) {
         events.removeEventListener('users', (event) => this.handleUsersEvent(event));
       }
     }
   },

   resetAlert() {
     this.showAlert = false;
   },

   openCompanyProfileForm() {
     if (!this.getSelectedCompany) {
       this.alertTitle = "Alert!";
       this.alertText = "You must first select a company from Owner Account.";
       this.showAlert = true;
     } else {
       this.companyForm.name = this.getSelectedCompany.name;
       this.companyForm.bbasMainPhone = this.getSelectedCompany.bbasMainPhone;
       this.companyForm.intakeNotificationEmails = this.getSelectedCompany.intakeNotificationEmails;
       this.companyForm.icon = this.getSelectedCompany.icon;
       this.companyProfileForm = true;
     }
   },

   closeCompanyProfileForm() {
     this.companyProfileForm = false;
   },

   async submitCompanyForm() {
     const formData = new FormData();
     formData.append('name', this.companyForm.name);
     formData.append('bbasMainPhone', this.companyForm.bbasMainPhone);
     formData.append('intakeNotificationEmails', this.companyForm.intakeNotificationEmails);
     formData.append('companyNumber', this.getSelectedCompany.companyNumber);
     if (this.companyForm.icon) {
       formData.append('icon', this.companyForm.icon);
     }

     try {
       const response = await axios.put(
         `${process.env.VUE_APP_THIS_SERVER}/portaldb/db/companies/${this.getSelectedCompany._id}`, 
         formData
       );
       
       const updatedCompany = response.data.company;
       console.log('Company update response:', updatedCompany);
       
       this.updateSelectedCompany(updatedCompany);
       this.closeCompanyProfileForm();
     } catch (error) {
       console.error('Error updating company:', error);
       this.alertTitle = "Error";
       this.alertText = "Failed to update company profile. Please try again.";
       this.showAlert = true;
     }
   },

   openDialog(message) {
     this.dialogMessage = message;
     this.dialog = true;
   },

   closeDialog() {
     this.dialog = false;
   },

   async fetchUsers() {
     try {
       const companyNumber = this.getSelectedCompany?.companyNumber;
       console.log('Fetching users for companyNumber:', companyNumber);
       const response = await axios.get(`/portaldb/db/users/${companyNumber}`);
       this.users = response.data;
       console.log('Fetched users:', this.users);
     } catch (error) {
       console.error('Error fetching users:', error);
     }
   },

   async openAddUserForm() {
     this.isEditMode = false;
     this.form = { id: null, name: '', email: '', role: '' };

     console.log('Current userRole from Vuex getter:', this.userRole);

     this.roleOptions = this.userRole === 'Owner' ? ['Owner', 'Administrator', 'User'] : ['Administrator', 'User'];

     console.log('Role options:', this.roleOptions);

     this.showFormDialog = true;
   },

   editUser(user) {
     if (this.userRole === 'Administrator' && user.role === 'Owner') {
       this.openDialog('You do not have permissions to edit an Owner account.');
       return;
     }

     this.isEditMode = true;
     
     this.form = {
       _id: user._id,
       name: user.name,
       email: user.email,
       role: user.role
     };

     this.roleOptions = this.userRole === 'Owner' ? ['Owner', 'Administrator', 'User'] : ['Administrator', 'User'];

     this.showFormDialog = true;
   },

   confirmDeleteUser(user) {
     if (this.userRole === 'Administrator' && user.role === 'Owner') {
       this.alertTitle = "Permission Denied";
       this.alertText = 'You do not have permissions to delete an Owner account.';
       this.showAlert = true;
       return;
     }

     if (user.uid === this.user.uid) {
       this.alertTitle = "Action Not Allowed";
       this.alertText = 'You cannot delete your own account.';
       this.showAlert = true;
       return;
     }

     this.setSelectedUser(user);
     this.showDeleteDialog = true;
   },

   closeForm() {
     this.submitClicked = false;
     this.showFormDialog = false;
   },

   async submitForm() {
     if (!this.isAddUserFormValid) {
       this.showValidationAnimation = true;
       setTimeout(() => {
         this.showValidationAnimation = false;
       }, 500);
       return;
     }

     this.submitClicked = true;

     try {
       this.form.companyNumber = this.getSelectedCompany?.companyNumber || '';

       console.log('Submitting form:', this.form);

       let updatedUser;
       if (this.isEditMode) {
         console.log('Editing existing user');
         const response = await axios.put(`/portaldb/db/users/${this.form._id}`, {
           name: this.form.name,
           role: this.form.role
         });
         updatedUser = response.data.user;
         console.log('User updated:', updatedUser);

         if (this.getSelectedUser && this.getSelectedUser._id === updatedUser._id) {
           console.log('Updating selectedUser in Vuex store');
           await this.setSelectedUser(updatedUser);
           console.log('Updated selectedUser in Vuex store:', this.getSelectedUser);
         }
         
         await this.fetchUsers();
       } else {
         console.log('Adding new user');
         const response = await axios.post('/portaldb/db/users', {
           name: this.form.name,
           email: this.form.email,
           role: this.form.role,
           companyNumber: this.form.companyNumber
         });
         updatedUser = response.data.user;
         await this.fetchUsers();
       }

       this.closeForm();

     } catch (error) {
       console.error('Error submitting form:', error);
       this.alertTitle = "Error";
       this.alertText = "Failed to submit user form. Please try again.";
       this.showAlert = true;
     }
   },

    selectUser(user, index) {
      if (!user.uid) {
        this.dialogMessage = "SELECT A DIFFERENT USER. This user has never logged in. To select a user, they must have logged in at least once. The system will not work for them until they have logged in at least once.";
        this.dialog = true;
        return;
      }
      this.selectedUserIndex = index;
      this.setSelectedUser(user);
      console.log('Selected user in CompanyAccount.vue:', user);
    },

   closeDeleteDialog() {
     this.showDeleteDialog = false;
   },

   async deleteUser() {
     if (!this.getSelectedUser) {
       return;
     }

     if (this.getSelectedUser.uid === this.user.uid) {
       this.alertTitle = "Action Not Allowed";
       this.alertText = 'You cannot delete your own account.';
       this.showAlert = true;
       this.closeDeleteDialog();
       return;
     }

     try {
       await axios.delete(`/portaldb/db/users/${this.getSelectedUser._id}`, { data: { uid: this.getSelectedUser.uid, role: this.userRole } });
       await this.fetchUsers();
       this.closeDeleteDialog();
       this.setSelectedUser(this.user);
     } catch (error) {
       console.error('Error deleting user:', error);
       this.alertTitle = "Error";
       this.alertText = 'An error occurred while deleting the user. Please try again.';
       this.showAlert = true;
     }
   },
 },

 async mounted() {
   await this.fetchSelectedCompany();
   await this.fetchUsers();
   this.listenForUsersUpdates();
 },

 beforeUnmount() {
   this.unlistenForUsersUpdates();
 }
};

</script>

<style scoped>
.standout {
 font-size:18px;
 color: #333333;
 font-weight: bold;
}

.body-text {
 font-size: 18px;
 color: gray;
}

.user-list {
 margin-top: 20px;
 max-height: 400px;
 overflow-y: auto;
}

.user-row {
 display: flex;
 align-items: center;
 padding: 10px;
 cursor: pointer;
 transition: background-color 0.3s;
}

.user-row.selected {
 background-color: #d3d3d3 !important;
}

.user-row.header {
 font-weight: bold;
 background-color: #f0f0f0 !important;
 cursor: default;
}

.user-details {
 display: flex;
 align-items: center;
 width: 100%;
}

.user-name {
 width: 150px;
 flex-shrink: 0;
 padding: 0 10px;
}

.user-email {
 width: 250px;
 flex-shrink: 0;
 padding: 0 10px;
}

.user-dialed-in,
.user-talking {
 width: 100px;
 flex-shrink: 0;
 text-align: center;
 padding: 0 10px;
}

.user-role {
 width: 120px;
 flex-shrink: 0;
 padding: 0 10px;
}

.status-dot {
 font-size: 24px;
 line-height: 1;
}

.status-dot.dialed-in,
.status-dot.talking {
 color: #d2b48c;
}

.edit-button,
.delete-button {
 margin-left: 20px;
 background-color: #d2b48c;
}

.add-button {
 background-color: #d2b48c;
}

.jail-calls-container {
 max-height: 600px;
 overflow-y: auto;
 background-color: #f5f5dc;
}

:deep(.v-expansion-panels > .v-expansion-panel > .v-expansion-panel-title) {
 font-size: 1.25rem !important;
 font-weight: 600 !important;
 color: #ffffff !important;
 background-color: #d2b48c !important;
 border: 1px solid #d2b48c !important;
 min-height: 64px !important;
 padding: 16px !important;
}

:deep(.v-expansion-panels > .v-expansion-panel > .v-expansion-panel-title .v-expansion-panel-title__icon .v-icon) {
 color: #ffffff !important;
}

:deep(.v-expansion-panel-text) {
 background-color: #f5f5dc !important;
 border: 1px solid #d2b48c !important;
 border-top: none !important;
}

#jail-calls-panel {
 margin-bottom: 8px;
}

#documents-panel {
 margin-bottom: 8px;
}

.shake-animation {
 animation: shake 0.5s cubic-bezier(.36,.07,.19,.97) both;
}

@keyframes shake {
 10%, 90% { transform: translate3d(-1px, 0, 0); }
 20%, 80% { transform: translate3d(2px, 0, 0); }
 30%, 50%, 70% { transform: translate3d(-4px, 0, 0); }
 40%, 60% { transform: translate3d(4px, 0, 0); }
}

@media (max-width: 600px) {
 .user-list {
   overflow-x: auto;
 }

 .user-row {
   width: 1100px;
   flex-shrink: 0;
 }

 .user-details {
   flex: 1;
   min-width: 150px;
   overflow: hidden;
   text-overflow: ellipsis;
   white-space: nowrap;
 }

 .edit-button,
 .delete-button {
   flex-shrink: 0;
   width: 70px;
   margin-left: 5px;
 }
}
</style>
